/**
 * CanvasPoseOverlayPlayer - Frame-accurate pose overlay using requestVideoFrameCallback
 *
 * This component uses the modern requestVideoFrameCallback API to achieve
 * frame-perfect synchronization between video playback and pose overlay rendering.
 *
 * Unlike the old approach using timeupdate (~4x/sec) or requestAnimationFrame (60Hz),
 * requestVideoFrameCallback fires exactly when each video frame is displayed,
 * providing the exact frame timing via metadata.mediaTime.
 *
 * Browser Support (92% global):
 * - Chrome 83+, Edge 83+, Safari 15.4+, Firefox 132+
 * - Fallback to requestAnimationFrame for older browsers
 */

import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Play, Pause, SkipBack, SkipForward, Settings, Maximize, AlertCircle, Pencil } from 'lucide-react';
import { drawPoseSkeleton } from './PoseRenderer';
import * as authService from './services/authService';
import './CanvasPoseOverlayPlayer.css';

// Check if requestVideoFrameCallback is supported
const supportsRVFC = typeof HTMLVideoElement !== 'undefined' &&
  'requestVideoFrameCallback' in HTMLVideoElement.prototype;

const clampFrameNumber = (frameNumber, maxFrameNumber) => {
  const safeFrameNumber = Number.isFinite(frameNumber) ? Math.round(frameNumber) : 0;
  const safeMaxFrameNumber = Number.isFinite(maxFrameNumber) ? Math.max(0, maxFrameNumber) : null;

  if (safeMaxFrameNumber === null) {
    return Math.max(0, safeFrameNumber);
  }

  return Math.max(0, Math.min(safeFrameNumber, safeMaxFrameNumber));
};

const CanvasPoseOverlayPlayer = forwardRef(({
  videoId,
  videoUrl,
  totalFrames,
  fps = 30,
  onFrameChange,
  apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000',
  hideControls = false,
  drawMode = false,
  onDrawToggle,
}, ref) => {
  // Player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);

  // Pose data state
  const [poseData, setPoseData] = useState({});
  const [isLoadingPoses, setIsLoadingPoses] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loadedFrameCount, setLoadedFrameCount] = useState(0);

  // Effective FPS: validated against actual video metadata
  const [effectiveFps, setEffectiveFps] = useState(fps);

  // Sync effectiveFps when fps prop changes (e.g., when analysis result provides actual FPS)
  useEffect(() => {
    setEffectiveFps(fps);
  }, [fps]);

  // UI state
  const [showSettings, setShowSettings] = useState(false);

  // Overlay controls auto-hide state (landscape fullscreen)
  const [controlsVisible, setControlsVisible] = useState(true);
  const controlsTimerRef = useRef(null);

  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const rvfcCallbackRef = useRef(null);
  const seekFnRef = useRef(null);
  const seekAsyncFnRef = useRef(null);
  const seekHandlerRef = useRef(null);
  const poseDataRef = useRef(poseData);
  const pendingPlayPromiseRef = useRef(null);
  const pendingPlayRequestedRef = useRef(false);
  const playRequestTokenRef = useRef(0);

  // Edit-list offset: MP4 containers from yt-dlp/YouTube often have "empty edits"
  // that insert dead time before the first video frame. The browser honors these
  // (video.currentTime includes the delay), but OpenCV/MediaPipe ignores them
  // (frame timestamps start at 0). This ref stores the detected offset so we can
  // convert between browser time and raw media time.
  const mediaTimeOffsetRef = useRef(0);
  const mediaTimeOffsetDetectedRef = useRef(false);

  // Browser presentation time → raw media time (for pose/frame lookup)
  const toMediaTime = useCallback(
    (browserTime) => Math.max(0, browserTime - mediaTimeOffsetRef.current),
    [],
  );

  // Raw media time → browser presentation time (for seeking)
  const toBrowserTime = useCallback(
    (mediaTime) => mediaTime + mediaTimeOffsetRef.current,
    [],
  );

  const pauseVideoSafely = useCallback(() => {
    const video = videoRef.current;
    const shouldInterruptPendingPlay =
      pendingPlayRequestedRef.current || pendingPlayPromiseRef.current !== null;

    playRequestTokenRef.current += 1;
    pendingPlayRequestedRef.current = false;
    pendingPlayPromiseRef.current = null;

    if (video && (!video.paused || shouldInterruptPendingPlay)) {
      video.pause();
    }

    setIsPlaying(false);
  }, []);

  const playVideoSafely = useCallback(async (reason = 'playback') => {
    const video = videoRef.current;
    if (!video) return false;

    if (pendingPlayRequestedRef.current) {
      if (pendingPlayPromiseRef.current) {
        try {
          await pendingPlayPromiseRef.current;
        } catch {
          return false;
        }
      }
      return true;
    }

    if (!video.paused && !video.ended) {
      return true;
    }

    const requestToken = playRequestTokenRef.current + 1;
    playRequestTokenRef.current = requestToken;
    pendingPlayRequestedRef.current = true;

    let playPromise;

    try {
      playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        pendingPlayPromiseRef.current = playPromise;
        await playPromise;
        if (playRequestTokenRef.current === requestToken && pendingPlayPromiseRef.current === playPromise) {
          pendingPlayPromiseRef.current = null;
        }
      }
      return true;
    } catch (error) {
      if (playRequestTokenRef.current === requestToken) {
        pendingPlayPromiseRef.current = null;
        pendingPlayRequestedRef.current = false;
      }

      const isAbortError =
        error?.name === 'AbortError' ||
        /aborted/i.test(String(error?.message || ''));

      if (isAbortError) {
        console.warn(`Playback request was interrupted before start (${reason}).`, error);
        return false;
      }

      console.error(`Playback request failed (${reason}):`, error);
      return false;
    }
  }, []);

  // Expose imperative API for comparison viewer
  useImperativeHandle(ref, () => ({
    seekToFrame: (frame) => seekFnRef.current?.(frame),
    seekToFrameAsync: (frame) => seekAsyncFnRef.current?.(frame),
    seekByFrames: (offset) => seekByFrames(offset),
    play: () => {
      void playVideoSafely('imperative play');
    },
    pause: () => {
      pauseVideoSafely();
    },
    getCurrentFrame: () => currentFrame,
    getVideoMetadata: () => ({ fps: effectiveFps, total_frames: totalFrames || loadedFrameCount, duration }),
    isVideoPlaying: () => isPlaying,
    getContainerElement: () => containerRef.current?.querySelector('.video-canvas-container'),
    getPoseDataForFrame: (frame) => poseDataRef.current[frame] || null,
  }));

  // Pre-load all pose data before playback
  useEffect(() => {
    const loadPoseData = async () => {
      if (!videoId) return;

      setIsLoadingPoses(true);
      setLoadError(null);

      try {
        console.log(`Loading pose data for video ${videoId}...`);
        const startTime = performance.now();

        // Fetch all frames in one request using the existing API
        const response = await fetch(
          `${apiBaseUrl}/api/v1/videos/${videoId}/frames`,
          {
            headers: {
              ...authService.getAuthHeader(),
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        const frames = data.frames || [];

        // Convert to frame-indexed dictionary for O(1) lookup
        const poseDict = {};
        for (const frame of frames) {
          if (frame && frame.frame_number !== undefined) {
            poseDict[frame.frame_number] = {
              frame_number: frame.frame_number,  // Include frame_number for sync
              pose_landmarks: frame.pose_landmarks || frame.pose_data?.landmarks,
              pose_confidence: frame.pose_confidence || 0.8,
              joint_angles: frame.joint_angles,
              timestamp: frame.timestamp,
              coaching: frame.coaching || null,
            };
          }
        }

        const loadTime = performance.now() - startTime;
        console.log(`Loaded ${Object.keys(poseDict).length} frames in ${loadTime.toFixed(0)}ms`);

        setPoseData(poseDict);
        poseDataRef.current = poseDict;
        setLoadedFrameCount(Object.keys(poseDict).length);
        setIsLoadingPoses(false);

      } catch (error) {
        console.error('Failed to load pose data:', error);
        setLoadError(error.message);
        setIsLoadingPoses(false);
      }
    };

    loadPoseData();
  }, [videoId, apiBaseUrl]);

  useEffect(() => () => {
    const video = videoRef.current;
    if (video && seekHandlerRef.current) {
      video.removeEventListener('seeked', seekHandlerRef.current);
    }
    seekHandlerRef.current = null;
  }, []);

  // Handle video metadata loaded - only set dimensions, don't draw yet
  // Drawing must wait for readyState >= 2 (HAVE_CURRENT_DATA) to have actual pixel data
  const handleVideoLoad = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (video && canvas) {
      setDuration(video.duration);

      // Match canvas size to video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Set container aspect ratio to match video so canvas overlay aligns perfectly
      const container = containerRef.current?.querySelector('.video-canvas-container');
      if (container && video.videoWidth > 0 && video.videoHeight > 0) {
        container.style.aspectRatio = `${video.videoWidth} / ${video.videoHeight}`;
      }

      // FPS validation: only auto-detect if no backend FPS was provided (fps prop = default 30)
      if (fps === 30 && totalFrames > 0 && video.duration > 0) {
        // No explicit FPS from backend — try to calculate from totalFrames/duration
        const calculatedFps = totalFrames / video.duration;
        // Only override default 30 if calculated is significantly different
        if (Math.abs(calculatedFps - 30) > 3) {
          console.warn(`FPS auto-detected: ${calculatedFps.toFixed(2)} (no backend FPS available)`);
          setEffectiveFps(calculatedFps);
        }
        // else: calculated ~30, keep effectiveFps as-is (already synced from fps prop)
      }
      // else: fps was explicitly provided or no totalFrames available — already synced via useEffect

      console.log(`Video metadata loaded: ${video.videoWidth}x${video.videoHeight}, ${video.duration}s, fps=${fps}, totalFrames=${totalFrames}`);
      // Note: Don't draw here - loadedmetadata fires before frame data is available
    }
  }, [fps, totalFrames]);

  // Draw video frame + skeleton overlay onto canvas
  const drawFrame = useCallback((frameNumber, videoElement, canvasElement) => {
    const ctx = canvasElement?.getContext('2d');
    if (!ctx) return;

    const width = canvasElement.width;
    const height = canvasElement.height;

    // Draw the video frame as background (decoded correctly even on inter-frames)
    if (videoElement && videoElement.readyState >= 2) {
      ctx.drawImage(videoElement, 0, 0, width, height);
    } else {
      ctx.clearRect(0, 0, width, height);
      return; // Don't draw skeleton if video frame isn't decoded yet
    }

    // Draw pose skeleton on top
    const framePose = poseData[frameNumber];
    if (framePose && framePose.pose_landmarks) {
      drawPoseSkeleton(ctx, framePose, width, height, {
        connectionColor: '#00FF00',
        landmarkColor: '#00FF00',
        connectionWidth: 2,
        landmarkRadius: 4,
        confidenceThreshold: 0.5
      });
    }
  }, [poseData]);

  const getMaxFrameNumber = useCallback(() => {
    const candidates = [
      Number.isFinite(totalFrames) && totalFrames > 0 ? totalFrames - 1 : null,
      Number.isFinite(loadedFrameCount) && loadedFrameCount > 0 ? loadedFrameCount - 1 : null,
      Number.isFinite(duration) && duration > 0
        ? Math.max(0, Math.round(duration * effectiveFps) - 1)
        : null,
    ].filter((value) => Number.isFinite(value) && value >= 0);

    return candidates.length > 0 ? Math.max(...candidates) : 0;
  }, [duration, effectiveFps, loadedFrameCount, totalFrames]);

  // Draw initial frame once pose data is loaded
  // Canvas renders both video frame and skeleton
  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas || isLoadingPoses || Object.keys(poseData).length === 0) return;

    const drawInitialSkeleton = () => {
      // Ensure canvas is sized correctly
      if (canvas.width === 0 || canvas.height === 0) {
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 360;
      }

      if (video.videoWidth === 0 || video.videoHeight === 0) return;

      const frameNumber = clampFrameNumber(
        toMediaTime(video.currentTime) * effectiveFps,
        getMaxFrameNumber(),
      );
      drawFrame(frameNumber, video, canvas);
    };

    // Browsers often have a bug where canvas drawImage yields black if the video
    // hasn't fully painted its first frame yet, even if readyState >= 2.
    // The most robust fix is to force a tiny seek which triggers hardware decode.
    //
    // We also detect the MP4 edit-list offset here. Videos from yt-dlp/YouTube
    // often have "empty edits" that push the first real frame to a non-zero
    // presentation time. The offset between that time and 0 tells us how to
    // convert between browser time and the raw frame timestamps stored in the DB.
    const detectOffsetAndDraw = (firstFrameTime) => {
      if (!mediaTimeOffsetDetectedRef.current) {
        // Threshold 0.03s: any real edit-list offset is ≥ one frame duration
        const offset = firstFrameTime > 0.03 ? firstFrameTime : 0;
        mediaTimeOffsetRef.current = offset;
        mediaTimeOffsetDetectedRef.current = true;
        if (offset > 0) {
          console.log(
            `[CanvasOverlay] Edit-list offset detected: ${(offset * 1000).toFixed(1)}ms — ` +
            `first real frame at ${firstFrameTime.toFixed(4)}s`
          );
        }
      }
      drawInitialSkeleton();
    };

    const forceFrameRender = () => {
      // Try RVFC first — it reports the exact mediaTime of the presented frame
      if (typeof video.requestVideoFrameCallback === 'function') {
        video.requestVideoFrameCallback((_, metadata) => {
          detectOffsetAndDraw(metadata?.mediaTime ?? video.currentTime);
        });
      }

      const onSeeked = () => {
        video.removeEventListener('seeked', onSeeked);
        // If RVFC didn't fire yet (rare), detect from currentTime
        if (!mediaTimeOffsetDetectedRef.current) {
          detectOffsetAndDraw(video.currentTime);
        } else {
          drawInitialSkeleton();
        }
        // Secondary fallback for Safari/iOS just in case
        setTimeout(drawInitialSkeleton, 50);
      };
      video.addEventListener('seeked', onSeeked);

      // Nudge to 0.001s to force seek event. If already at 0.001, setting it triggers it again.
      video.currentTime = 0.001;
    };

    // Handler for waiting until video frame data is ready
    const handler = () => {
      if (video.readyState >= 2) {
        forceFrameRender();
        video.removeEventListener('canplay', handler);
        video.removeEventListener('loadeddata', handler);
      }
    };

    // If video has current frame data ready, force render now
    if (video.readyState >= 2) {
      forceFrameRender();
    } else {
      // Wait for HAVE_CURRENT_DATA (readyState >= 2) so video frame pixels are available
      video.addEventListener('canplay', handler);
      video.addEventListener('loadeddata', handler);
    }
    return () => {
      video.removeEventListener('canplay', handler);
      video.removeEventListener('loadeddata', handler);
    };
  }, [drawFrame, effectiveFps, getMaxFrameNumber, isLoadingPoses, poseData, toMediaTime]);

  // requestVideoFrameCallback handler - frame-accurate sync
  const onVideoFrame = useCallback((_, metadata) => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    // Convert browser presentation time to raw media time (edit-list corrected)
    const correctedTime = toMediaTime(metadata.mediaTime);
    const calculatedFrame = Math.round(correctedTime * effectiveFps);
    const framePose = poseData[calculatedFrame];

    // Use pose data's frame_number if available, otherwise fall back to calculated
    const reportedFrame = framePose?.frame_number ?? calculatedFrame;

    setCurrentFrame(reportedFrame);
    setCurrentTime(metadata.mediaTime);

    // Draw the frame
    drawFrame(calculatedFrame, video, canvas);

    // Notify parent with pose data's frame number for consistency
    if (onFrameChange) {
      onFrameChange(reportedFrame, framePose);
    }

    // Request next frame callback
    rvfcCallbackRef.current = video.requestVideoFrameCallback(onVideoFrame);
  }, [effectiveFps, drawFrame, onFrameChange, poseData, toMediaTime]);

  // requestAnimationFrame fallback for older browsers
  const rafFallback = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas || video.paused) return;

    // Convert browser presentation time to raw media time (edit-list corrected)
    const correctedTime = toMediaTime(video.currentTime);
    const calculatedFrame = Math.round(correctedTime * effectiveFps);
    const framePose = poseData[calculatedFrame];

    // Use pose data's frame_number if available, otherwise fall back to calculated
    const reportedFrame = framePose?.frame_number ?? calculatedFrame;

    setCurrentFrame(reportedFrame);
    setCurrentTime(video.currentTime);

    drawFrame(calculatedFrame, video, canvas);

    if (onFrameChange) {
      onFrameChange(reportedFrame, framePose);
    }

    animationFrameRef.current = requestAnimationFrame(rafFallback);
  }, [effectiveFps, drawFrame, onFrameChange, poseData, toMediaTime]);

  // Start/stop sync based on playback state
  useEffect(() => {
    const video = videoRef.current;
    if (!video || isLoadingPoses) return;

    if (isPlaying) {
      if (supportsRVFC) {
        rvfcCallbackRef.current = video.requestVideoFrameCallback(onVideoFrame);
      } else {
        animationFrameRef.current = requestAnimationFrame(rafFallback);
      }
    } else {
      // Cleanup when paused
      if (rvfcCallbackRef.current && video.cancelVideoFrameCallback) {
        video.cancelVideoFrameCallback(rvfcCallbackRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }

    return () => {
      if (rvfcCallbackRef.current && video?.cancelVideoFrameCallback) {
        video.cancelVideoFrameCallback(rvfcCallbackRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, isLoadingPoses, onVideoFrame, rafFallback]);

  // Playback controls
  const togglePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying || pendingPlayRequestedRef.current) {
      pauseVideoSafely();
    } else {
      void playVideoSafely('play toggle');
    }
  }, [isPlaying, pauseVideoSafely, playVideoSafely]);

  const seekToFrameInternal = useCallback((frameNumber) => {
    const video = videoRef.current;
    if (!video) return Promise.resolve();

    const requestedFrame = clampFrameNumber(frameNumber, getMaxFrameNumber());
    // Convert raw media time to browser presentation time (add edit-list offset)
    const time = toBrowserTime(requestedFrame / effectiveFps);
    const canvas = canvasRef.current;

    if (time >= 0 && time <= video.duration) {
      if (isPlaying) {
        setCurrentFrame(requestedFrame);
        setCurrentTime(time);

        if (onFrameChange) {
          onFrameChange(requestedFrame, poseDataRef.current[requestedFrame] || null);
        }

        video.currentTime = time;
        return Promise.resolve();
      }

      setCurrentFrame(requestedFrame);
      setCurrentTime(time);

      if (onFrameChange) {
        onFrameChange(requestedFrame, poseDataRef.current[requestedFrame] || null);
      }

      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          video.removeEventListener('seeked', handleSeeked);
          if (seekHandlerRef.current === handleSeeked) {
            seekHandlerRef.current = null;
          }
          resolve();
        }, 500);

        function handleSeeked() {
          clearTimeout(timeout);
          video.removeEventListener('seeked', handleSeeked);
          if (seekHandlerRef.current === handleSeeked) {
            seekHandlerRef.current = null;
          }
          drawFrame(requestedFrame, video, canvas);
          resolve();
        }

        if (seekHandlerRef.current) {
          video.removeEventListener('seeked', seekHandlerRef.current);
        }
        seekHandlerRef.current = handleSeeked;
        video.addEventListener('seeked', handleSeeked);
        video.currentTime = time;
      });
    }

    return Promise.resolve();
  }, [
    drawFrame,
    effectiveFps,
    getMaxFrameNumber,
    isPlaying,
    onFrameChange,
    toBrowserTime,
  ]);

  const seekToFrame = useCallback((frameNumber) => {
    void seekToFrameInternal(frameNumber);
  }, [seekToFrameInternal]);
  // Keep ref current so useImperativeHandle always delegates to latest closure
  seekFnRef.current = seekToFrame;

  // Async version that resolves when the browser finishes seeking (with canvas draw)
  const seekToFrameAsync = useCallback((frameNumber) => seekToFrameInternal(frameNumber), [seekToFrameInternal]);
  seekAsyncFnRef.current = seekToFrameAsync;

  const seekByFrames = useCallback((offset) => {
    const maxFrame = getMaxFrameNumber() + 1;
    const newFrame = Math.max(0, Math.min(currentFrame + offset, maxFrame - 1));
    seekToFrame(newFrame);
  }, [currentFrame, getMaxFrameNumber, seekToFrame]);

  // Timeline click handler
  const handleTimelineClick = useCallback((event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const percentage = (event.clientX - rect.left) / rect.width;
    const newTime = percentage * duration;
    // Convert browser time to raw media time for frame calculation
    const newFrame = Math.round(toMediaTime(newTime) * effectiveFps);
    seekToFrame(newFrame);
  }, [duration, effectiveFps, seekToFrame, toMediaTime]);

  // Auto-hide controls overlay
  const showControlsTemporarily = useCallback(function() {
    setControlsVisible(function(prev) {
      if (!prev) return true;
      return prev;
    });
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    controlsTimerRef.current = setTimeout(function() {
      if (videoRef.current && !videoRef.current.paused) {
        setControlsVisible(false);
      }
    }, 3000);
  }, []);

  useEffect(function() {
    if (isPlaying) {
      showControlsTemporarily();
    } else {
      setControlsVisible(true);
      if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    }
    return function() {
      if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    };
  }, [isPlaying, showControlsTemporarily]);

  // Force controls visible during draw mode
  useEffect(() => {
    if (drawMode) {
      setControlsVisible(true);
      if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    }
  }, [drawMode]);

  const handleContainerMouseEnter = useCallback(() => {
    showControlsTemporarily();
  }, [showControlsTemporarily]);

  const handleContainerMouseMove = useCallback(() => {
    if (isPlaying) showControlsTemporarily();
  }, [isPlaying, showControlsTemporarily]);

  const handleContainerMouseLeave = useCallback(() => {
    if (isPlaying) setControlsVisible(false);
  }, [isPlaying]);

  // Tap-to-toggle controls on video container
  const handleVideoContainerClick = useCallback(() => {
    if (drawMode) return; // Don't toggle controls in draw mode
    if (controlsVisible) {
      setControlsVisible(false);
      if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    } else {
      showControlsTemporarily();
    }
  }, [controlsVisible, showControlsTemporarily]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (event) => {
      switch (event.key) {
        case ' ':
          event.preventDefault();
          togglePlayPause();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          seekByFrames(-1);
          break;
        case 'ArrowRight':
          event.preventDefault();
          seekByFrames(1);
          break;
        case 'j':
          seekByFrames(-10);
          break;
        case 'l':
          seekByFrames(10);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [togglePlayPause, seekByFrames]);

  // Fullscreen toggle - use native fullscreen on desktop, inline on mobile
  const [isInlineFullscreen, setIsInlineFullscreen] = useState(false);

  // Detect if device is mobile/touch
  const isMobileDevice = useCallback(() => {
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      window.matchMedia('(max-width: 768px)').matches
    );
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (isMobileDevice()) {
      // Mobile: use inline fullscreen (CSS-based)
      setIsInlineFullscreen(prev => {
        if (prev) setControlsVisible(true); // Reset on exit
        return !prev;
      });
    } else {
      // Desktop: native fullscreen + inline-fullscreen CSS for proper layout
      if (!document.fullscreenElement) {
        containerRef.current?.requestFullscreen();
        setIsInlineFullscreen(true);
      } else {
        document.exitFullscreen();
      }
    }
  }, [isMobileDevice]);

  // Handle escape key and body scroll for inline fullscreen, and sync native fullscreen exit
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isInlineFullscreen) {
        // If in native fullscreen, browser handles Escape -> fullscreenchange clears state
        if (!document.fullscreenElement) {
          setIsInlineFullscreen(false);
        }
      }
    };

    // Sync isInlineFullscreen when native fullscreen exits (Escape, F11, etc.)
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && isInlineFullscreen) {
        setIsInlineFullscreen(false);
        setControlsVisible(true);
      }
    };

    if (isInlineFullscreen) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent body scroll when in fullscreen
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.body.style.overflow = '';
    };
  }, [isInlineFullscreen]);

  // Format time helper
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Render loading state
  if (isLoadingPoses) {
    return (
      <div className="canvas-pose-player loading">
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading pose data for {totalFrames || '?'} frames...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (loadError) {
    return (
      <div className="canvas-pose-player error">
        <AlertCircle size={48} />
        <p>Failed to load pose data</p>
        <p className="error-detail">{loadError}</p>
      </div>
    );
  }

  return (
    <div
      className={`canvas-pose-player ${isInlineFullscreen ? 'inline-fullscreen' : ''}`}
      ref={containerRef}
      onMouseEnter={handleContainerMouseEnter}
      onMouseMove={handleContainerMouseMove}
      onMouseLeave={handleContainerMouseLeave}
    >
      {/* Video + Canvas container */}
      <div className="video-canvas-container" onClick={handleVideoContainerClick}>
        {/* Hidden video element - canvas renders the frame */}
        <video
          ref={videoRef}
          src={videoUrl}
          onLoadedMetadata={handleVideoLoad}
          onPlay={() => {
            pendingPlayRequestedRef.current = false;
            pendingPlayPromiseRef.current = null;
            setIsPlaying(true);
          }}
          onPause={() => {
            pendingPlayRequestedRef.current = false;
            pendingPlayPromiseRef.current = null;
            setIsPlaying(false);
          }}
          onEnded={() => {
            pendingPlayRequestedRef.current = false;
            pendingPlayPromiseRef.current = null;
            setIsPlaying(false);
          }}
          onError={() => {
            const mediaError = videoRef.current?.error;
            const code = mediaError?.code ?? 'unknown';
            const detail = mediaError?.message || `media error code ${code}`;
            setLoadError(`Video load failed: ${detail}`);
          }}
          playsInline
          webkitplaysinline="true"
          muted
          controls={false}
          disablePictureInPicture
          controlsList="nodownload nofullscreen noremoteplayback"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
            opacity: 0,
            position: 'absolute',
          }}
        />

        {/* Canvas renders both video frame and skeleton overlay */}
        <canvas
          ref={canvasRef}
          className="pose-canvas"
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
        />
      </div>

      {/* Controls overlay - auto-hides when playing, hover/tap to show */}
      {!hideControls && (
      <div
        className={`controls-overlay overlay-mode ${controlsVisible ? 'visible' : 'hidden'}`}
        onTouchStart={(e) => { e.stopPropagation(); showControlsTemporarily(); }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Timeline */}
        <div className="timeline-container">
          <div className="timeline" onClick={handleTimelineClick}>
            <div className="timeline-track">
              <div
                className="timeline-progress"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              />
              <div
                className="timeline-thumb"
                style={{ left: `${(currentTime / duration) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Control buttons */}
        <div className="player-controls">
          <div className="main-controls">
            {/* Back 1 frame - always shown, use arrow symbol */}
            <button onClick={() => seekByFrames(-1)} className="control-btn frame-step-btn" title="Back 1 frame">
              ◀
            </button>

            {/* Back 30 frames - only in non-fullscreen */}
            {!isInlineFullscreen && (
              <button onClick={() => seekByFrames(-30)} className="control-btn" title="Back 30 frames">
                <SkipBack size={20} />
              </button>
            )}

            <button onClick={togglePlayPause} className="play-pause-btn">
              {isPlaying ? <Pause size={24} /> : <Play size={24} />}
            </button>

            {/* Forward 30 frames - only in non-fullscreen */}
            {!isInlineFullscreen && (
              <button onClick={() => seekByFrames(30)} className="control-btn" title="Forward 30 frames">
                <SkipForward size={20} />
              </button>
            )}

            {/* Forward 1 frame - always shown, use arrow symbol */}
            <button onClick={() => seekByFrames(1)} className="control-btn frame-step-btn" title="Forward 1 frame">
              ▶
            </button>

            <div className="time-display">
              <span>{formatTime(currentTime)} / {formatTime(duration)}</span>
              <span className="frame-display">Frame: {currentFrame}</span>
            </div>
          </div>

          <div className="secondary-controls">
            {onDrawToggle && (
              <button onClick={onDrawToggle} className={`control-btn${drawMode ? ' active' : ''}`} title="Draw mode">
                <Pencil size={18} />
              </button>
            )}
            <button onClick={toggleFullscreen} className="control-btn">
              <Maximize size={18} />
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Settings panel - removed as per user request */}
    </div>
  );
});

export default CanvasPoseOverlayPlayer;
